import { FormikProvider, useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import * as Yup from 'yup';
import { StepperComponent } from '../../../../_metronic/assets/ts/components';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { forgotPassword } from '../core/_requests';
import Step1 from './forgot-password/Step1';
import Step2 from './forgot-password/Step2';

const initialValues = {
  email: '',
};

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Formato de email inválido')
    .min(3, 'Mínimo de 3 caracteres')
    .max(50, 'Máximo de 50 caracteres')
    .required('Email é obrigatório'),
});

export function ForgotPassword() {
  const stepperRef = useRef<HTMLFormElement | null>(null);
  const [stepper, setStepper] = useState<StepperComponent | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const loadStepper = () => {
    setStepper(StepperComponent.createInsance(stepperRef.current as HTMLFormElement));
  };

  const submitStep = async (values: { email: string }) => {
    if (!stepper) return;

    if (currentStep === 1) {
      setLoading(true);
      try {
        await forgotPassword(values.email);

        SystemNotification.success('E-mail enviado com sucesso!');
        stepper.goNext();
        setCurrentStep(2);
      } catch (error) {
        SystemNotification.error(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: submitStep,
  });

  useEffect(() => {
    if (!stepperRef.current) return;
    loadStepper();
  }, [stepperRef]);

  return (
    <FormikProvider value={formik}>
      <form
        ref={stepperRef}
        noValidate
        className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
        id="kt_login_password_reset_form"
        onSubmit={formik.handleSubmit}
      >
        {currentStep === 1 && (
          <div className="current" data-kt-stepper-element="content">
            <Step1 formik={formik} />
          </div>
        )}

        {currentStep === 2 && (
          <div className="current" data-kt-stepper-element="content">
            <Step2 email={formik.values.email} />
          </div>
        )}

        <div className="d-flex flex-wrap justify-content-center pb-lg-0 mt-10">
          {currentStep === 1 && (
            <>
              <button className="btn btn-primary me-4" disabled={loading} id="kt_password_reset_submit" type="submit">
                <span className="indicator-label" style={{ display: loading ? 'none' : 'inline' }}>
                  Enviar
                </span>
                <span className="indicator-progress" style={{ display: loading ? 'inline' : 'none' }}>
                  <span className="spinner-border spinner-border-sm align-middle ms-2" />
                </span>
              </button>
              <Link to="/auth/login">
                <button className="btn btn-light" id="kt_login_password_reset_form_cancel_button" type="button">
                  Cancelar
                </button>
              </Link>
            </>
          )}

          {currentStep === 2 && (
            <div className="d-flex flex-column justify-content-center align-items-center">
              <Link to="/auth">
                <button className="btn btn-sm btn-primary mb-12" type="button">
                  Ir para página de login
                </button>
              </Link>
              <div>
                <svg fill="none" height="160" viewBox="0 0 180 223" width="140" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M111.778 22.0134L48.6641 68.0925L115.824 112.122L178.581 68.0925L111.778 22.0134Z"
                    fill="black"
                  />
                  <path d="M60.5651 0.785645H59.375V63.7006H60.5651V0.785645Z" fill="black" />
                  <path d="M161.206 6.09253H160.016V63.8836H161.206V6.09253Z" fill="black" />
                  <path d="M78.5377 24.9048H77.3477V51.9154H78.5377V24.9048Z" fill="black" />
                  <path d="M124.87 8.47168H123.68V35.1529H124.87V8.47168Z" fill="black" />
                  <path
                    d="M70.404 74.8271C70.0867 73.8755 68.9759 73.3631 67.9445 73.6559C66.9131 73.9487 66.3577 74.9735 66.675 75.9251C66.9924 76.8767 68.1032 77.3891 69.1346 77.0963C70.166 76.8035 70.761 75.7787 70.404 74.8271Z"
                    fill="white"
                  />
                  <path
                    d="M72.2287 54.8069L82.8999 51.6227L93.0552 55.3193L93.8486 53.4161L86.2321 50.6345L93.3725 48.5117L92.9759 47.3772L84.3279 49.9391L77.1081 47.304L75.085 48.8045L80.9957 50.9273L71.832 53.6723L72.2287 54.8069Z"
                    fill="white"
                  />
                  <path
                    d="M78.298 64.4694L77.9806 62.4198L61.5575 64.3962L66.9526 58.9062L65.2864 57.552L58.1459 64.8354L52.1559 65.5673L48.8633 67.9463V68.0195L55.8451 67.1777L52.4732 70.6181L54.3377 71.8259L59.2567 66.7751L78.298 64.4694Z"
                    fill="white"
                  />
                  <path
                    d="M119.432 83.2816H119.234C117.369 88.918 113.68 91.7362 108.206 91.7362C104.675 91.7362 101.859 90.5284 99.7164 88.1494C97.5742 85.7704 96.5032 82.5496 96.5032 78.4871C96.5032 73.1435 97.9709 68.7149 100.946 65.1647C103.921 61.6146 107.809 59.8578 112.609 59.8578C114.473 59.8578 116.1 60.297 117.568 61.1754C118.996 62.0538 119.948 63.1518 120.344 64.4693H120.503C120.543 63.8106 120.702 62.4564 120.9 60.4068H125.7C124.51 73.4363 123.875 80.0608 123.875 80.317C123.875 85.2214 125.383 87.6736 128.437 87.6736C131.214 87.6736 133.515 86.2828 135.34 83.5378C137.164 80.7562 138.077 77.2061 138.077 72.7775C138.077 66.2627 135.855 60.9558 131.373 56.8566C126.89 52.7574 120.741 50.6712 112.847 50.6712C105.23 50.6712 98.9626 53.1234 93.9643 58.0644C89.0056 62.9688 86.5064 69.1175 86.5064 76.5107C86.5064 83.7574 88.8866 89.6134 93.6073 94.0786C98.328 98.5437 104.715 100.776 112.728 100.776C119.075 100.776 124.47 99.8247 128.953 97.8849V102.277C124.51 104.034 118.996 104.912 112.331 104.912C103.009 104.912 95.4717 102.314 89.799 97.1163C84.0866 91.9192 81.2305 85.1482 81.2305 76.7303C81.2305 68.0927 84.2056 60.9192 90.1957 55.2096C96.1858 49.5 103.802 46.6453 113.125 46.6453C121.852 46.6453 129.032 49.0242 134.784 53.8188C140.497 58.5768 143.353 64.8353 143.353 72.5213C143.353 78.1577 141.845 82.7692 138.83 86.356C135.816 89.9794 132.166 91.7728 127.802 91.7728C122.249 91.7362 119.432 88.918 119.432 83.2816ZM112.53 63.9935C109.356 63.9935 106.778 65.3843 104.834 68.1659C102.89 70.9475 101.898 74.4245 101.898 78.5603C101.898 81.3418 102.573 83.5378 103.882 85.1482C105.191 86.7586 106.976 87.5638 109.158 87.5638C112.331 87.5638 114.87 86.0998 116.695 83.1718C118.559 80.2438 119.472 76.3643 119.472 71.5697C119.511 66.5189 117.171 63.9935 112.53 63.9935Z"
                    fill="white"
                  />
                  <path
                    d="M112.373 105.205C102.971 105.205 95.3147 102.533 89.5627 97.2993C83.8106 92.0656 80.875 85.1116 80.875 76.6571C80.875 67.9463 83.9296 60.6264 89.9594 54.8802C95.9891 49.134 103.804 46.2061 113.166 46.2061C121.933 46.2061 129.311 48.6582 135.063 53.4528C140.855 58.284 143.751 64.6523 143.751 72.4115C143.751 78.0845 142.204 82.8424 139.149 86.4658C136.055 90.1624 132.247 92.029 127.804 92.029C122.449 92.029 119.513 89.5768 119.037 84.6724C116.974 89.5402 113.325 92.029 108.128 92.029C104.518 92.029 101.543 90.7846 99.361 88.3324C97.1792 85.8802 96.0684 82.5496 96.0684 78.4505C96.0684 73.0337 97.5758 68.4953 100.591 64.9085C103.606 61.2852 107.652 59.4552 112.571 59.4552C114.475 59.4552 116.221 59.931 117.728 60.8094C118.799 61.4682 119.632 62.2367 120.148 63.1517C120.227 62.4197 120.306 61.5048 120.425 60.297L120.465 59.9676H126.059L126.019 60.3702C124.551 76.4009 124.234 80.0242 124.194 80.2438C124.194 84.9286 125.543 87.1978 128.359 87.1978C131.017 87.1978 133.159 85.8802 134.905 83.2084C136.69 80.5 137.602 76.9499 137.602 72.6677C137.602 66.2993 135.381 61.029 131.017 57.003C126.654 52.977 120.505 50.9274 112.769 50.9274C105.311 50.9274 99.0436 53.3796 94.204 58.1742C89.3246 63.0053 86.8651 69.1175 86.8651 76.3643C86.8651 83.5012 89.2056 89.3206 93.8469 93.6759C98.4882 98.0313 104.796 100.264 112.69 100.264C118.918 100.264 124.313 99.3123 128.756 97.3725L129.311 97.1163V102.313L129.073 102.423C124.67 104.326 119.037 105.205 112.373 105.205ZM113.206 46.9746C104.042 46.9746 96.4255 49.8294 90.5544 55.3926C84.6833 60.9924 81.7081 68.1659 81.7081 76.6571C81.7081 84.892 84.5643 91.663 90.1577 96.7869C95.7511 101.874 103.249 104.473 112.412 104.473C118.839 104.473 124.313 103.631 128.637 101.984V98.3973C124.234 100.191 118.918 101.106 112.809 101.106C104.716 101.106 98.1709 98.8365 93.4106 94.2981C88.6106 89.7964 86.1908 83.794 86.1908 76.4741C86.1908 69.0443 88.7295 62.7491 93.7676 57.7716C98.8056 52.794 105.232 50.2686 112.928 50.2686C120.901 50.2686 127.209 52.3914 131.731 56.5638C136.253 60.7362 138.554 66.1895 138.554 72.7409C138.554 77.1695 137.602 80.866 135.738 83.6842C133.834 86.539 131.414 88.003 128.518 88.003C125.225 88.003 123.559 85.4044 123.559 80.2804C123.559 80.1706 123.559 79.9144 125.344 60.7362H121.258C121.06 62.6027 120.941 63.8471 120.902 64.4693L120.862 64.7987H120.029L119.949 64.5425C119.553 63.2981 118.68 62.2733 117.331 61.4682C115.943 60.6264 114.356 60.2238 112.611 60.2238C107.93 60.2238 104.121 61.944 101.265 65.3843C98.3693 68.8247 96.9015 73.2533 96.9015 78.487C96.9015 82.4398 97.9329 85.5874 100.035 87.8932C102.098 90.199 104.835 91.3336 108.207 91.3336C113.563 91.3336 117.014 88.6618 118.878 83.1352L118.958 82.879H119.83V83.245C119.87 88.6618 122.488 91.3336 127.883 91.3336C132.049 91.3336 135.658 89.5768 138.594 86.0632C141.53 82.5496 143.037 77.938 143.037 72.4481C143.037 64.9085 140.181 58.7232 134.587 54.0384C128.954 49.3536 121.774 46.9746 113.206 46.9746ZM109.159 87.9298C106.819 87.9298 104.954 87.0514 103.566 85.3678C102.217 83.7208 101.503 81.415 101.503 78.5602C101.503 74.3879 102.495 70.8011 104.518 67.9829C106.541 65.0915 109.239 63.6275 112.571 63.6275C117.45 63.6275 119.91 66.2993 119.91 71.5697C119.91 76.4375 118.958 80.3902 117.053 83.3548C115.11 86.3926 112.492 87.9298 109.159 87.9298ZM112.531 64.3595C109.477 64.3595 107.057 65.6771 105.153 68.3855C103.249 71.0939 102.257 74.5343 102.257 78.5602C102.257 81.2686 102.892 83.3914 104.161 84.9286C105.43 86.4658 107.057 87.1978 109.12 87.1978C112.174 87.1978 114.515 85.807 116.34 82.9888C118.164 80.134 119.077 76.2911 119.077 71.5697C119.116 66.7385 116.974 64.3595 112.531 64.3595Z"
                    fill="white"
                  />
                  <path d="M114.476 109.78L48.6641 68.0925V141.402H178.581V68.0925L114.476 109.78Z" fill="white" />
                  <path
                    d="M179.178 141.951H48.0703V67.0679L114.477 109.121L179.178 67.0312V141.951ZM49.2604 140.853H177.988V69.154L114.477 110.475L114.12 110.256L49.2207 69.154V140.853H49.2604Z"
                    fill="black"
                  />
                  <path
                    d="M127.263 101.943L126.508 102.792L178.089 141.9L178.845 141.052L127.263 101.943Z"
                    fill="black"
                  />
                  <path
                    d="M99.8433 100.89L48.2617 139.998L49.0173 140.846L100.599 101.738L99.8433 100.89Z"
                    fill="black"
                  />
                  <path d="M166.601 116.368H146.727V117.978H166.601V116.368Z" fill="white" />
                  <path
                    d="M123.916 128.007C124.836 128.007 125.582 127.318 125.582 126.469C125.582 125.62 124.836 124.932 123.916 124.932C122.996 124.932 122.25 125.62 122.25 126.469C122.25 127.318 122.996 128.007 123.916 128.007Z"
                    fill="black"
                  />
                  <path d="M40.6913 109.56L36.6055 117.063H38.4699L42.1592 109.56H40.6913Z" fill="black" />
                  <path d="M168.584 114.684H146.727V134.851H168.584V114.684Z" fill="black" />
                  <path d="M158.864 128.921L156.762 129.104L157.317 134.851H159.42L158.864 128.921Z" fill="white" />
                  <path
                    d="M168.585 127.53L156.842 118.637L156.168 119.405L167.91 128.262L162.198 131.3L163.07 132.691L168.585 129.763V127.53Z"
                    fill="white"
                  />
                  <path
                    d="M152.081 125.005C152.081 124.237 151.407 123.615 150.574 123.615C149.741 123.615 149.066 124.237 149.066 125.005C149.066 125.774 149.741 126.396 150.574 126.396C151.407 126.396 152.081 125.774 152.081 125.005Z"
                    fill="white"
                  />
                  <path
                    d="M73.8146 84.4526L74.6874 81.5613H83.9303V80.3535H75.0444L75.798 77.8647L72.0294 76.9131L70.9981 80.3535H67.5469L69.3716 81.5613H70.641L70.4031 82.2201L73.8146 84.4526Z"
                    fill="white"
                  />
                  <path
                    d="M94.8022 40.4597C95.4156 40.4597 95.9129 40.0009 95.9129 39.435C95.9129 38.869 95.4156 38.4102 94.8022 38.4102C94.1887 38.4102 93.6914 38.869 93.6914 39.435C93.6914 40.0009 94.1887 40.4597 94.8022 40.4597Z"
                    fill="white"
                  />
                  <path
                    d="M90.7152 170.096C95.6738 170.499 98.9268 137.559 112.573 139.316C122.53 140.597 119.991 153.224 116.262 165.302C112.732 176.831 110.352 206.952 52.4739 211.381L27.2441 222.214C27.2441 222.214 12.9234 212.625 0.824219 170.389L23.6738 161.642C23.6738 161.642 40.0573 128.299 51.7201 116.112C59.7333 107.73 65.8425 110.878 65.8425 110.878C65.8425 110.878 72.1895 100.776 83.535 107.511L89.5251 94.4079C89.5251 94.4079 93.7697 76.5106 100.196 73.7656C105.512 71.4965 113.961 76.5472 112.97 89.5767C111.899 103.448 107.535 129.031 101.862 142.793C95.9912 157.213 95.5548 159.812 90.7152 170.096Z"
                    fill="#6857A7"
                  />
                  <path
                    d="M83.2502 106.741L68.2266 142.661L69.3368 143.056L84.3604 107.137L83.2502 106.741Z"
                    fill="white"
                  />
                  <path
                    d="M66.8828 146.235L63.0469 149.99L63.913 150.743L67.7489 146.988L66.8828 146.235Z"
                    fill="white"
                  />
                  <path
                    d="M65.6431 110.036L53.332 133.204L54.4006 133.687L66.7117 110.519L65.6431 110.036Z"
                    fill="white"
                  />
                  <path
                    d="M50.9511 137.261L48.0586 140.61L48.9894 141.294L51.8819 137.945L50.9511 137.261Z"
                    fill="white"
                  />
                  <path
                    d="M115.312 159.556C113.566 159.043 112.336 158.018 111.662 156.554C109.441 151.796 113.407 143.818 113.606 143.452L114.677 143.927C114.637 144.001 110.75 151.833 112.773 156.115C113.328 157.323 114.28 158.092 115.708 158.531L115.312 159.556Z"
                    fill="white"
                  />
                  <path
                    d="M102.144 184.288L101.402 185.147L105.033 187.815L105.774 186.957L102.144 184.288Z"
                    fill="white"
                  />
                  <path
                    d="M98.5287 92.2485C97.4973 92.2485 96.3469 92.0655 95.1172 91.6629L95.5139 90.6381C98.0924 91.4433 100.155 91.2969 101.623 90.1989C105.828 87.0513 104.638 77.3524 104.638 77.2426L105.828 77.0962C105.868 77.5354 107.137 87.5271 102.416 91.0407C101.306 91.8459 99.9965 92.2485 98.5287 92.2485Z"
                    fill="white"
                  />
                  <path
                    d="M101.861 143.049C101.861 143.049 89.2458 138.254 86.6673 149.198C84.0888 160.141 90.7533 170.06 90.7533 170.06C90.7533 170.06 95.5533 160.178 101.861 143.049Z"
                    fill="black"
                  />
                </svg>
              </div>
            </div>
          )}
        </div>
      </form>
    </FormikProvider>
  );
}
